import { useState, useEffect } from 'react';
import { formatDateDDMMYY } from '../../utils/poExpenseHelpers';

/** Click-to-edit spreadsheet-style cell. Saves onBlur/Enter, reverts on Escape.
 * Pass `colorMap` ({ value: 'tailwind classes', __default: 'tailwind classes' })
 * to render the value as a coloured pill instead of plain text when not editing. */
export const EditableCell = ({ value, onSave, placeholder = '—', type = 'text', testId, colorMap }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(value || ''); }, [value]);

  const commit = async () => {
    setEditing(false);
    if (draft === (value || '')) return;
    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <input
        autoFocus
        type={type}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
        }}
        className="w-full px-1.5 py-1 text-xs border border-orange-300 rounded outline-none"
        data-testid={testId ? `${testId}-input` : undefined}
      />
    );
  }
  const badgeClass = value && colorMap ? (colorMap[value] || colorMap.__default) : null;
  const displayValue = value && type === 'date' ? formatDateDDMMYY(value) : value;
  return (
    <div
      onClick={() => setEditing(true)}
      className={`px-1.5 py-1 text-xs rounded cursor-text hover:bg-orange-50 min-h-[24px] transition-colors ${saving ? 'opacity-50' : ''}`}
      data-testid={testId ? `${testId}-value` : undefined}
    >
      {value ? (
        badgeClass ? (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border ${badgeClass}`}>{displayValue}</span>
        ) : (
          <span className="font-medium text-gray-800">{displayValue}</span>
        )
      ) : (
        <span className="text-gray-300">{placeholder}</span>
      )}
    </div>
  );
};

export default EditableCell;
